import axios from "axios";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Normalize: tolerate a URL entered without a scheme (a common deploy mistake —
// a schemeless value is treated as a relative path and silently hits the SPA
// fallback), and strip any trailing slash so we never build "…//api".
const BACKEND_URL = RAW_BACKEND_URL
  ? (/^https?:\/\//i.test(RAW_BACKEND_URL) ? RAW_BACKEND_URL : `https://${RAW_BACKEND_URL}`).replace(/\/+$/, "")
  : RAW_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

if (!BACKEND_URL) {
  // Loud, actionable message instead of a cryptic downstream crash. Without this
  // var, requests resolve against the SPA origin and the _redirects fallback
  // returns index.html (HTML, HTTP 200) — which the interceptor below rejects.
  // eslint-disable-next-line no-console
  console.error(
    "[api] REACT_APP_BACKEND_URL is not set. Set it to your backend's URL " +
      "(a Cloudflare Pages *build* variable) and redeploy — API calls cannot work without it."
  );
}

const client = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

// Any HTML response from a JSON API means the request never reached the backend
// (almost always: REACT_APP_BACKEND_URL unset/wrong, so the request hit the SPA
// fallback). Reject it with a clear message so callers' catch blocks show a
// clean error/empty state instead of feeding an HTML string into the UI.
client.interceptors.response.use((response) => {
  const contentType = response.headers?.["content-type"] || "";
  if (contentType.includes("text/html")) {
    throw new Error(
      "Backend unreachable: the API returned HTML, not JSON. REACT_APP_BACKEND_URL " +
        "is likely unset or wrong (requests are falling back to the app itself)."
    );
  }
  return response;
});

export const InvoiceAPI = {
  list: async ({ search = "", status = "" } = {}) => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    const res = await client.get("/invoices", { params });
    return res.data;
  },
  get: async (id) => (await client.get(`/invoices/${id}`)).data,
  verify: async (vid) => (await client.get(`/verify/${vid}`)).data,
  create: async (payload) => (await client.post("/invoices", payload)).data,
  update: async (id, payload) => (await client.put(`/invoices/${id}`, payload)).data,
  remove: async (id) => (await client.delete(`/invoices/${id}`)).data,
  analytics: async () => (await client.get("/invoices/analytics")).data,
};

export const SettingsAPI = {
  getCompany: async () => (await client.get("/settings/company")).data,
  updateCompany: async (payload) => (await client.put("/settings/company", payload)).data,
};

export const DocTypeAPI = {
  list: async ({ category = "", status = "" } = {}) => {
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    return (await client.get("/doc-types", { params })).data;
  },
  get: async (slug) => (await client.get(`/doc-types/${slug}`)).data,
};

export const DocumentAPI = {
  list: async ({ doc_type = "", status = "", search = "" } = {}) => {
    const params = {};
    if (doc_type) params.doc_type = doc_type;
    if (status) params.status = status;
    if (search) params.search = search;
    return (await client.get("/documents", { params })).data;
  },
  get: async (id) => (await client.get(`/documents/${id}`)).data,
  create: async (payload) => (await client.post("/documents", payload)).data,
  update: async (id, payload) => (await client.put(`/documents/${id}`, payload)).data,
  remove: async (id) => (await client.delete(`/documents/${id}`)).data,
  generate: async (id) => (await client.post(`/documents/${id}/generate`)).data,
  analytics: async () => (await client.get("/documents/analytics")).data,
  verify: async (vid) => (await client.get(`/verify-document/${vid}`)).data,
};

export const ForgeAPI = {
  analyze: async (filesOrFile) => {
    const fd = new FormData();
    const files = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    files.forEach((f) => fd.append("files", f));
    const res = await axios.post(`${API}/forge/analyze`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    });
    return res.data;
  },
  scope: async (report) => (await client.post("/forge/scope", { report })).data,
  quote: async (payload) => (await client.post("/forge/quote", payload)).data,
  rates: async () => (await client.get("/forge/rates")).data,
  refreshRates: async () => (await client.post("/forge/rates/refresh")).data,
};

export function formatCurrency(amount, currency = "CAD") {
  if (amount === null || amount === undefined || isNaN(amount)) return "$0.00";
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(Number(amount));
  const code = (currency || "CAD").toUpperCase();
  const symbol = code === "USD" || code === "CAD" || code === "AUD" ? "$" : code === "EUR" ? "€" : code === "GBP" ? "£" : "$";
  return `${sign}${symbol}${abs.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
